import React, { useRef } from 'react';
import { Database, ChevronDown, Printer, Download, FileUp, Table } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export interface AppHeaderDataDropdownProps {
  dataDropdownRef: React.RefObject<HTMLDivElement | null>;
  showDataDropdown: boolean;
  setShowDataDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setShowExportProModal: (show: boolean) => void;
  exportAsJSON: () => void;
  importFromJSON: (file: File) => Promise<any>;
  exportAsExcel: () => void;
}

export const AppHeaderDataDropdown: React.FC<AppHeaderDataDropdownProps> = ({
  dataDropdownRef,
  showDataDropdown,
  setShowDataDropdown,
  setShowExportProModal,
  exportAsJSON,
  importFromJSON,
  exportAsExcel,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImportClick = () => {
    fileInputRef.current?.click();
    setShowDataDropdown(false);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await importFromJSON(file);
      } catch (err) {
        console.error('Import failed:', err);
        alert('Import fehlgeschlagen. Bitte prüfen Sie die Datei.');
      }
    }
    e.target.value = '';
  };

  return (
    <>
      <div className="relative" ref={dataDropdownRef}>
        <button
          onClick={() => setShowDataDropdown(!showDataDropdown)}
          className={`p-2 rounded-xl transition-all active:scale-95 border flex items-center gap-1 group relative ${
            showDataDropdown
              ? 'bg-indigo-500/20 border-indigo-400 text-indigo-400 shadow-[0_0_15px_rgba(129,140,248,0.2)]'
              : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-indigo-400'
          }`}
          title="Datenverwaltung"
        >
          <Database size={18} />
          <ChevronDown size={14} className={`transition-transform duration-200 ${showDataDropdown ? 'rotate-180' : ''}`} />
          {!showDataDropdown && (
            <div className="absolute top-12 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 text-white text-[9px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow-xl border border-white/10">
              DATEN & BACKUP
            </div>
          )}
        </button>

        <AnimatePresence>
          {showDataDropdown && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 top-full mt-2 w-64 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl p-2 z-[100] backdrop-blur-xl"
            >
              <button
                onClick={() => {
                  setShowExportProModal(true);
                  setShowDataDropdown(false);
                }}
                className="w-full flex items-center gap-3 p-3 text-left bg-gradient-to-r from-indigo-500/10 to-purple-500/10 hover:from-indigo-500/20 hover:to-purple-500/20 text-indigo-400 dark:text-indigo-400 rounded-xl transition-all group border border-indigo-500/25 mb-1"
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-md shrink-0">
                  <Printer size={20} />
                </div>
                <div>
                  <p className="text-xs font-bold text-white">Export Pro (A3/A4)</p>
                  <p className="text-[10px] text-slate-400">PDF, Excel & JSON Backup</p>
                </div>
              </button>

              <div className="h-px bg-white/5 my-1" />

              <button
                onClick={() => {
                  exportAsJSON();
                  setShowDataDropdown(false);
                }}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/5 rounded-xl transition-all group"
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform">
                  <Download size={20} />
                </div>
                <div>
                  <p className="text-xs font-bold text-white">Export (JSON)</p>
                  <p className="text-[10px] text-slate-400">Vollständiges JSON-Backup</p>
                </div>
              </button>

              <button
                onClick={handleImportClick}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/5 rounded-xl transition-all group"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform">
                  <FileUp size={20} />
                </div>
                <div>
                  <p className="text-xs font-bold text-white">Import (JSON)</p>
                  <p className="text-[10px] text-slate-400">JSON-Datei wiederherstellen</p>
                </div>
              </button>

              <div className="h-px bg-white/5 my-1" />

              <button
                onClick={() => {
                  exportAsExcel();
                  setShowDataDropdown(false);
                }}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/5 rounded-xl transition-all group opacity-60 hover:opacity-100"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400">
                  <Table size={20} />
                </div>
                <div>
                  <p className="text-xs font-bold text-white">Excel Export</p>
                  <p className="text-[10px] text-slate-400">Alle Projekte als XLSX</p>
                </div>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Hidden File Input for JSON restore */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </>
  );
};
